/** TOTAL: stable stock-first ordering of the entire filtered collection, then pagination. */
export const STOCK_PARAM = 'filter.v.availability';
const MODE_PARAM = 'jd1_stock';
const sequenceConfig = () => document.querySelector('jd1-stock-sequence');
export function normalizeCollectionURL(input, config = sequenceConfig(), reset = false) {
  const url = new URL(input, window.location.origin);
  if (!config) return url;
  if (!url.searchParams.get('sort_by') && config.dataset.defaultSort) url.searchParams.set('sort_by', config.dataset.defaultSort);
  if (config.dataset.enabled === 'true') {
    // Migrate V1 phase URLs. V2 carries one global page and doesn't impose a stock filter.
    if (url.searchParams.get(MODE_PARAM) === '1') {
      url.searchParams.delete(STOCK_PARAM);
      url.searchParams.delete('page');
    }
    url.searchParams.set(MODE_PARAM, '2');
  } else {
    if (url.searchParams.get(MODE_PARAM) === '1') url.searchParams.delete(STOCK_PARAM);
    url.searchParams.delete(MODE_PARAM);
  }
  if (reset) url.searchParams.delete('page');
  return url;
}
export function normalizeCollectionFacets(parameters) {
  const config = sequenceConfig();
  if (!config) return parameters;
  const next = new URL(window.location.href);
  next.search = parameters.toString();
  return normalizeCollectionURL(next, config, true).searchParams;
}
export function pageTarget(base, page) {
  const url = new URL(base);
  url.searchParams.set(MODE_PARAM, '2');
  url.searchParams.set('page', String(page));
  url.searchParams.delete('section_id');
  url.searchParams.delete('sections');
  url.hash = 'ResultsList';
  return url;
}
export function buildStockIndex(chunks, partial = false) {
  const available = [];
  const soldOut = [];
  const seen = new Set();
  const orderedChunks = [...chunks].sort((a, b) => a.offset - b.offset);
  let offset = 0;
  for (const chunk of orderedChunks) {
    if (chunk.offset !== offset || chunk.count !== orderedChunks[0].count) throw new Error('Collection changed while loading');
    for (const item of chunk.items) {
      const id = String(item[0]);
      if (!/^\d+$/.test(id) || typeof item[1] !== 'boolean' || seen.has(id)) throw new Error('Invalid collection index');
      seen.add(id);
      const entry = { id, available: item[1], nativeIndex: offset++ };
      (entry.available ? available : soldOut).push(entry);
    }
  }
  if (!partial && offset !== orderedChunks[0].count) throw new Error('Incomplete collection index');
  return available.concat(soldOut);
}
export function planStockPage(index, requestedPage, pageSize, totalCount = index.length) {
  const pages = Math.ceil(totalCount / pageSize);
  const page = Math.max(1, Math.min(pages || 1, Number(requestedPage) || 1));
  const entries = index.slice((page - 1) * pageSize, page * pageSize);
  const nativePages = [...new Set(entries.map(item => Math.floor(item.nativeIndex / pageSize) + 1))];
  return { page, pages, entries, nativePages, count: totalCount };
}
async function inBatches(items, callback, size = 4) {
  const output = [];
  for (let start = 0; start < items.length; start += size) {
    output.push(...await Promise.all(items.slice(start, start + size).map(callback)));
  }
  return output;
}
function sectionURL(base, sectionId, page) {
  const url = new URL(base);
  url.searchParams.set('section_id', sectionId);
  url.searchParams.delete('sections');
  url.searchParams.set('page', String(page));
  url.hash = '';
  return url;
}
function stale() { const error = new Error('Collection changed while loading'); error.code = 'STALE'; return error; }
async function getSection(url, signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, {once:true});
  if (signal?.aborted) controller.abort();
  let timedOut = false;
  const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 12000);
  try {
    const response = await fetch(url.href, { signal:controller.signal });
    if (!response.ok) throw new Error('Collection section unavailable');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  } catch (error) {
    if (timedOut) throw new Error('La colección tardó demasiado en responder.');
    throw error;
  } finally {
    clearTimeout(timeout); signal?.removeEventListener('abort', abort);
  }
}
// Native availability pagination only requests card pages intersecting the visible global page.
export function planAvailabilityPage(available, sold, requestedPage, pageSize) {
  const count = available + sold;
  const pages = Math.ceil(count / pageSize);
  const page = Math.max(1, Math.min(pages || 1, Math.floor(Number(requestedPage) || 1)));
  const start = (page - 1) * pageSize, end = Math.min(start + pageSize, count);
  const segments = [];
  for (const [phase, offset, size] of [['1',0,available], ['0',available,sold]]) {
    let cursor = Math.max(start,offset) - offset;
    const stop = Math.min(end,offset + size) - offset;
    while (cursor < stop) {
      const localOffset = cursor % pageSize;
      const take = Math.min(stop - cursor, pageSize - localOffset);
      segments.push({phase, page:Math.floor(cursor/pageSize)+1, offset:localOffset, take, count:size});
      cursor += take;
    }
  }
  return {page,pages,count,segments};
}
async function loadIndex(base, signal, requiredItems) {
  const load = async page => {
    const html = await getSection(sectionURL(base, 'jd1-stock-index', page), signal);
    const data = JSON.parse(html.querySelector('[data-jd1-stock-index]')?.textContent || 'null');
    if (!data || !Array.isArray(data.items) || !Number.isInteger(data.pages) || !Number.isInteger(data.offset) || !Number.isInteger(data.count)) throw new Error('Invalid stock metadata');
    return data;
  };
  const first = await load(1);
  if (first.count > 25000) throw new Error('Collection exceeds Liquid pagination limit');
  const chunks = [first];
  let available = first.items.filter(item => item[1]).length;
  let next = 2;
  // Stop as soon as the available prefix contains the requested page. No full-catalog wait.
  while (available < requiredItems && next <= first.pages) {
    const pages = Array.from({length:Math.min(4,first.pages-next+1)},(_,i)=>next+i);
    const batch = await inBatches(pages,load);
    chunks.push(...batch); available += batch.reduce((sum,chunk)=>sum+chunk.items.filter(item=>item[1]).length,0);
    next += pages.length;
  }
  const complete = chunks.reduce((sum,chunk)=>sum+chunk.items.length,0) === first.count;
  return {index:buildStockIndex(chunks,!complete), count:first.count};
}
class JD1StockSequence extends HTMLElement {
  static observedAttributes = ['data-count', 'data-page', 'data-default-sort', 'data-enabled', 'data-sort', 'data-filters', 'data-stock-count', 'data-sold-count'];
  connectedCallback() { this.schedule(); }
  disconnectedCallback() { this.request?.abort(); clearTimeout(this.pending); }
  attributeChangedCallback() { this.schedule(); }
  schedule() {
    clearTimeout(this.pending);
    this.pending = setTimeout(() => { if (this.isConnected) this.render(); }, 0);
  }
  setState(root, state, message) {
    root.dataset.jd1StockState = state;
    const grid = root.querySelector('.product-grid');
    const blocked = state === 'loading' || state === 'error';
    if (grid) {
      grid.inert = blocked;
      grid.setAttribute('aria-busy', String(state === 'loading'));
      if (blocked) grid.setAttribute('aria-hidden', 'true'); else grid.removeAttribute('aria-hidden');
    }
    this.replaceChildren();
    if (message) {
      const status = document.createElement('p');
      status.className = 'jd1-stock-caption';
      status.setAttribute('role', state === 'error' ? 'alert' : 'status');
      status.textContent = message;
      this.append(status);
    }
  }
  nativeShortcut(root, url, pageSize, requestedPage) {
    if (this.rendered || this.dataset.sort !== url.searchParams.get('sort_by') || Number(this.dataset.page) !== requestedPage) return null;
    const count = Number(this.dataset.count);
    if (!Number.isInteger(count) || count < 0) return null;
    const pages = Math.ceil(count / pageSize);
    if (requestedPage > Math.max(1,pages)) return null;
    const cards = [...root.querySelectorAll('.product-grid > [data-product-id]')];
    const expected = Math.min(pageSize,Math.max(0,count-(requestedPage-1)*pageSize));
    if (cards.length !== expected) return null;
    const allAvailable = cards.every(card=>card.dataset.productAvailable === 'true');
    const allSold = cards.every(card=>card.dataset.productAvailable === 'false');
    const values = url.searchParams.getAll(STOCK_PARAM).flatMap(value=>value.split(','));
    const variantFilters = [...url.searchParams.keys()].some(key=>key.startsWith('filter.v.') && key!==STOCK_PARAM);
    const homogeneous = !variantFilters && this.dataset.supported === 'true' && (
      (allAvailable && (Number(this.dataset.stockCount) === count || (values.length === 1 && values[0] === '1'))) ||
      (allSold && (Number(this.dataset.soldCount) === count || (values.length === 1 && values[0] === '0')))
    );
    if (!count || (requestedPage === 1 && allAvailable) || homogeneous) return {cards,plan:{page:requestedPage,pages,count}};
    return null;
  }
  async availabilityCards(url, pageSize, requestedPage, signal) {
    if (this.dataset.supported !== 'true') return null;
    // A variant-specific filter can match an unavailable variant of an otherwise available product.
    if ([...url.searchParams.keys()].some(key=>key.startsWith('filter.v.') && key!==STOCK_PARAM)) return null;
    const count = Number(this.dataset.count);
    let available = Number(this.dataset.stockCount), sold = Number(this.dataset.soldCount);
    const values = [...new Set(url.searchParams.getAll(STOCK_PARAM).flatMap(value=>value.split(',')))];
    if (values.length === 1) {
      if (values[0] === '1') {available=count;sold=0;}
      else if (values[0] === '0') {available=0;sold=count;}
      else return null;
    }
    if (![count,available,sold].every(n=>Number.isInteger(n)&&n>=0) || available+sold!==count) return null;
    const plan = planAvailabilityPage(available,sold,requestedPage,pageSize);
    const batches = await Promise.all(plan.segments.map(async segment => {
      const target = sectionURL(url,this.dataset.sectionId,segment.page);
      target.searchParams.set(STOCK_PARAM,segment.phase);
      const html = await getSection(target,signal);
      const metadata = html.querySelector('jd1-stock-sequence');
      const cards = [...html.querySelectorAll('.product-grid > [data-product-id]')];
      if (!metadata || metadata.dataset.phase !== segment.phase || Number(metadata.dataset.count)!==segment.count || Number(metadata.dataset.page)!==segment.page || metadata.dataset.sort !== url.searchParams.get('sort_by')) throw stale();
      const expected = Math.min(pageSize,segment.count-(segment.page-1)*pageSize);
      if (cards.length !== expected || cards.some(card=>(card.dataset.productAvailable==='true') !== (segment.phase==='1'))) throw stale();
      return cards.slice(segment.offset,segment.offset+segment.take);
    }));
    const cards = batches.flat();
    if (new Set(cards.map(card=>card.dataset.productId)).size !== cards.length) throw stale();
    return {cards,plan};
  }
  async indexedCards(root, url, pageSize, requestedPage, signal) {
    for (let attempt=0;attempt<2;attempt++) {
      try {
        const {index,count} = await loadIndex(url,signal,requestedPage*pageSize);
        const plan = planStockPage(index,requestedPage,pageSize,count);
        const cards = new Map();
        const canReuse = attempt===0 && this.dataset.sort===url.searchParams.get('sort_by') && !this.rendered;
        if (canReuse) for (const card of root.querySelectorAll('.product-grid > [data-product-id]')) cards.set(card.dataset.productId,card);
        const needed = plan.nativePages.filter(page=>!canReuse || page!==Number(this.dataset.page));
        const documents = await inBatches(needed,page=>getSection(sectionURL(url,this.dataset.sectionId,page),signal));
        for (const html of documents) for (const card of html.querySelectorAll('.product-grid > [data-product-id]')) cards.set(card.dataset.productId,card);
        const ordered = plan.entries.map(entry=>{
          const card=cards.get(entry.id);
          if (!card || (card.dataset.productAvailable==='true')!==entry.available) throw stale();
          return card;
        });
        return {cards:ordered,plan};
      } catch(error) { if (error.code!=='STALE' || attempt===1) throw error; }
    }
  }
  async render() {
    this.request?.abort();
    const request = this.request = new AbortController();
    const root = this.closest('results-list');
    if (!root) return;
    const input = new URL(window.location.href);
    const url = normalizeCollectionURL(input,this);
    // Normalizing sort/default parameters no longer reloads the whole storefront.
    if (url.href !== input.href && !window.Shopify?.designMode) window.history.replaceState(window.history.state,'',url.href);
    const activeURL = window.location.href;
    if (this.dataset.enabled !== 'true') { this.setState(root,'native'); return; }
    const pageSize = Math.max(1,Number(this.dataset.pageSize)||24);
    const requestedPage = Math.max(1,Math.floor(Number(url.searchParams.get('page'))||1));
    const native = this.nativeShortcut(root,url,pageSize,requestedPage);
    if (native) { this.rendered=true; this.setState(root,'ready'); this.renderPagination(url,native.plan); return; }
    this.setState(root,'loading','Cargando productos…');
    try {
      let result;
      try { result=await this.availabilityCards(url,pageSize,requestedPage,request.signal); }
      catch(error) { if(error.code!=='STALE') throw error; }
      if (!result) result=await this.indexedCards(root,url,pageSize,requestedPage,request.signal);
      if (!this.isConnected || request.signal.aborted || window.location.href!==activeURL) return;
      const {cards,plan}=result;
      if (plan.page!==requestedPage && !window.Shopify?.designMode) {
        url.searchParams.set('page',String(plan.page));
        window.history.replaceState(window.history.state,'',pageTarget(url,plan.page).href);
      }
      const grid=root.querySelector('.product-grid');
      if (plan.count && !grid) throw new Error('Product grid missing');
      if (grid) {
        const orderedCards=cards.map((card,index)=>{
          const copy=document.importNode(card,true);
          copy.dataset.page=String(plan.page);
          copy.className=copy.className.replace(/product-grid__item--\d+/g,`product-grid__item--${index}`);
          return copy;
        });
        grid.replaceChildren(...orderedCards);grid.dataset.lastPage=String(plan.pages);
      }
      this.rendered=true;this.setState(root,'ready');this.renderPagination(url,plan);
    } catch(error) {
      if (error.name==='AbortError' || !this.isConnected || request.signal.aborted || window.location.href!==activeURL) return;
      this.setState(root,'error','No pudimos cargar los productos. Inténtalo nuevamente.');
      const button=document.createElement('button');button.type='button';button.className='jd1-stock-retry';button.textContent='REINTENTAR';
      button.addEventListener('click',()=>this.render());this.append(button);
    }
  }
  renderPagination(url, plan) {
    if (!plan.count) return;
    const caption = document.createElement('p');
    caption.className = 'jd1-stock-caption';
    caption.textContent = `Página ${plan.page} de ${plan.pages} · ${plan.count} productos`;
    this.append(caption);
    if (plan.pages <= 1) return;
    const nav = document.createElement('nav');
    nav.className = 'jd1-stock-navigation';
    nav.setAttribute('aria-label', 'Paginación de productos');
    const link = (number, label) => {
      const element = document.createElement(number === plan.page ? 'span' : 'a');
      element.textContent = label || String(number);
      if (number === plan.page) element.setAttribute('aria-current', 'page');
      else element.href = pageTarget(url, number).href;
      nav.append(element);
    };
    if (plan.page > 1) link(plan.page - 1, 'Anterior');
    const pages = [...new Set([1, plan.page - 1, plan.page, plan.page + 1, plan.pages])].filter(n => n > 0 && n <= plan.pages).sort((a,b) => a-b);
    let previous = 0;
    for (const page of pages) {
      if (previous && page - previous > 1) { const dots = document.createElement('span'); dots.textContent = '…'; nav.append(dots); }
      link(page); previous = page;
    }
    if (plan.page < plan.pages) link(plan.page + 1, 'Siguiente');
    this.append(nav);
  }
}
if (!customElements.get('jd1-stock-sequence')) {
  customElements.define('jd1-stock-sequence', JD1StockSequence);
  window.addEventListener('popstate', () => { if (sequenceConfig()) window.location.reload(); });
}
